import React, {useMemo, useState, useEffect, useRef} from 'react';
import {useSelector} from "react-redux";
import B2BApiService from "../../B2bApiService";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Text, DropdownSelect } from '../../components/formComponents/Inputs';
import WriteAccessGuard from '../../components/reusableElementsComponents/WriteAccessGuard';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import * as Constants from '../../constants'
import { toast } from 'react-toastify';
import Modal from 'react-bootstrap/Modal';
import * as Utils from '../../Utilities';
import { t } from '../../content';

export default function UserList({userRole, userEmail}) {
  const reduxCompanyUsers = useSelector(state => state.company.users || []);

  const hasFetchedRef = useRef(false);

  const b2bServiceObj = useMemo(() => {
    return new B2BApiService();
  }, []);

  const [inviteUserData, setInviteUserData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role_id: '',
    role_title: ''
  });

  const [inviteUserErrorMessages, setInviteUserErrorMessages] = useState({
    first_name: null,
    last_name: null,
    email: null,
    role_id: null
  });
  
  const [jobRoles, setJobRoles] = useState([]); // State variable for job roles
  const [users, setUsers] = useState([]); // All corporate users in the company
  const [currentUser, setCurrentUser] = useState(null); // Current logged in user info
  const [openEditUserModal, setOpenEditUserModal] = useState(false);
  const [editedUser, setEditedUser] = useState(null);
  const [editedUserErrorMessages, setEditedUserErrorMessages] = useState({
    first_name: null,
    last_name: null,
    role_id: null
  });

  const findRoleBySelectValue = (value) =>
    jobRoles.find((role) => String(role.id) === String(value) || role.title === value);
  
  useEffect(() => {
    b2bServiceObj.GetCorporateUserRoles((data) => {
      const formattedJobRoles = data.map((jobRole) => ({
        id: jobRole.id,
        title: jobRole.name || "",
      }));
      setJobRoles(formattedJobRoles); // Exclude HR Manager role (id: 4)
    })
  }, [b2bServiceObj]); // Empty dependency array ensures this effect runs only once on component mount

  useEffect(() => {
    if (reduxCompanyUsers && reduxCompanyUsers.length > 0 && reduxCompanyUsers[0].hasOwnProperty('id')) {
      setUsers(reduxCompanyUsers);
      setCurrentUser(reduxCompanyUsers.find(user => user.email === userEmail));
    }
  }, [reduxCompanyUsers, userEmail]);

  useEffect(() => {
      if (users.length === 0 && !hasFetchedRef.current) {
        hasFetchedRef.current = true;

        b2bServiceObj.GetCompanyAccounts(
          (data) => { // Success callback
            setUsers(data);
            setCurrentUser(data.find(user => user.email === userEmail));
        }, (errorResponse) => { // Error callback
          toast.error(errorResponse?.message || t('iam.toasts.loadUsersError'), {
            containerId: 'IAMToast'
          });
        });
      }
      
  }, [b2bServiceObj, users, userEmail]);

  const updateUserAccount = (userId, updatedData) => {
    // First Name validation
    const validateFirstName = Utils.validateField('text', editedUser.first_name);
    if (!validateFirstName.isValid) {
      setEditedUserErrorMessages(prevErrorMessages => ({...prevErrorMessages, first_name: validateFirstName.errorMessage}));
    }
    // Last Name validation
    const validateLastName = Utils.validateField('text', editedUser.last_name);
    if (!validateLastName.isValid) {
      setEditedUserErrorMessages(prevErrorMessages => ({...prevErrorMessages, last_name: validateLastName.errorMessage}));
    }
    // Role validation
    const validateRole = Utils.validateField('item', editedUser.role_id);
    if (!validateRole.isValid) {
      setEditedUserErrorMessages(prevErrorMessages => ({...prevErrorMessages, role_id: validateRole.errorMessage}));
    }
    // If any validation fails, return early
    if (!validateFirstName.isValid || !validateLastName.isValid || !validateRole.isValid) {
      return;
    }
    b2bServiceObj.UpdateUserAccount(userId, updatedData, 
      (successResponse) => { // Success callback
        // Update local user list
        const updatedUsers = users.map((user) => {
          if (user.id === userId) {
            return {
              ...user,
              first_name: updatedData.first_name || updatedData.first || user.first_name,
              last_name: updatedData.last_name || updatedData.last || user.last_name,
              role_id: updatedData.role_id || user.role_id,
              role_title: Constants.getUserRoleStringByRoleId(updatedData.role_id) || user.role_title
            };
          }
          return user;
        });
        setUsers(updatedUsers);

        b2bServiceObj.GetCompanyAccounts(); // Refresh user list from server 

        setOpenEditUserModal(false);

        toast.success(successResponse?.message || t('iam.toasts.userUpdated'),
          {
            containerId: 'IAMToast'
          }
        );
    }, (errorResponse) => { // Error callback
        toast.error(errorResponse?.message || t('iam.toasts.userUpdateError'),
          {
            containerId: 'IAMToast'
          }
        );
      }
    );
  };

  const deleteUserAccount = (userId) => {
    // SHOW ALERT TO CONFIRM DELETION. ONLY PROCEED IF CONFIRMED
    if (!window.confirm(t('iam.deleteUserConfirm'))) {
      return;
    }

    b2bServiceObj.DeleteUserAccount(userId,
      (successResponse)=>{ // Success callback
        const updatedUsers = users.filter((user) => user.id !== userId); // Update local user list
        setUsers(updatedUsers);

        b2bServiceObj.GetCompanyAccounts(); // Refresh user list from server

        setOpenEditUserModal(false);

        toast.success(successResponse?.message || t('iam.toasts.userRemoved'),
          {
            containerId: 'IAMToast',
          }
        );
      }, (errorResponse) => { // Error callback
        toast.error(errorResponse?.message || t('iam.toasts.userRemoveError'),
          {
            containerId: 'IAMToast',
          }
        );
      }
    );
  };

  const toggleEditUserModal = (userId) => {
    // Open edit user modal, which has all editable fields: user name, email, role dropdown (similar to invite user), region dropdown, 
    // 1 save button, 1 cancel button, 1 delete user button (red, only appear if user role is Owner and Admin; when clicked, show confirmation modal "Are you sure you want to delete this user? This action cannot be undone." with Yes/No buttons)

    const userToEdit = users.find((user) => user.id === userId);
    setEditedUser(userToEdit);
    setOpenEditUserModal(true);
  }

  const sendInvite = () =>{
    // First Name validation
    const validateFirstName = Utils.validateField('text', inviteUserData.first_name);
    if (!validateFirstName.isValid) {
      setInviteUserErrorMessages(prevErrorMessages => ({...prevErrorMessages, first_name: validateFirstName.errorMessage}));
    }
    // Last Name validation
    const validateLastName = Utils.validateField('text', inviteUserData.last_name);
    if (!validateLastName.isValid) {
      setInviteUserErrorMessages(prevErrorMessages => ({...prevErrorMessages, last_name: validateLastName.errorMessage}));
    }
    // Email validation. Not allowed yet. TODO: Need logging system to track this activity, send email invitation, send removal email notification
    const validateEmail = Utils.validateField('email', inviteUserData.email);
    if (!validateEmail.isValid) {
      setInviteUserErrorMessages(prevErrorMessages => ({...prevErrorMessages, email: validateEmail.errorMessage}));
    }
    // Role validation
    const validateRole = Utils.validateField('item', inviteUserData.role_id);
    if (!validateRole.isValid) {
      setInviteUserErrorMessages(prevErrorMessages => ({...prevErrorMessages, role_id: validateRole.errorMessage}));
    }
    
    // If any validation fails, return early
    if (!validateFirstName.isValid || !validateLastName.isValid || !validateRole.isValid) {
      return;
    }

    b2bServiceObj.CreateNewUserAccount({first_name: inviteUserData.first_name, last_name: inviteUserData.last_name, email : inviteUserData.email, role_id : inviteUserData.role_id}, (response) => {
      if(response.httpStatus === 200){
          let newUser = {
              id : response.id,
              name: `${response.first_name || response.first || ''} ${response.last_name || response.last || ''}`,
              first_name: response.first_name || response.first || '',
              last_name: response.last_name || response.last || '',
              email: response.email,
              role_id: response.role_id,
              role_title: Constants.getUserRoleStringByRoleId(response.role_id), // Default to "Unknown" if role_id is not found
          }
          const updatedArray = [newUser, ...users];
          setUsers(updatedArray);

          b2bServiceObj.GetCompanyAccounts(); // Refresh user list from server 

          setInviteUserData({
            first_name: '',
            last_name: '',
            email: '',
            role_id: '',
            role_title: ''
          });
          setInviteUserErrorMessages({
            first_name: null,
            last_name: null,
            email: null,
            role_id: null
          });

          toast.success(
            t('iam.toasts.inviteSent'),
            {
              containerId: 'IAMToast'
            }
        );
      }else{
        toast.error(
            t('iam.toasts.inviteError'),
            {
              containerId: 'IAMToast'
            }
        );
      }
    })
  }

  const actionTemplate = (rowData) => {
    let canEdit = true;
    let isCurrentUser = rowData.email === userEmail;

    if(
      rowData.role_id === Constants.USER_ROLE_OWNER || // If role_id = 1 (Owner), do not show edit icon
      (rowData.role_id === Constants.USER_ROLE_ADMIN && userRole !== Constants.USER_ROLE_OWNER) || // If role_id = 2 (Admin), and current user is not Owner, do not show edit icon
      (rowData.role_id === Constants.USER_ROLE_HRD && !Constants.ADMINISTRATIVE_USER_ROLES.includes(userRole)) // If role_id = 3 (HR Director), and current user is not Admin or Owner, do not show edit icon
    ){
      canEdit = false;
    }

    return (
      canEdit ? 
      <IconButton aria-label="edit" onClick={() => toggleEditUserModal(rowData.id)}>
        <EditIcon />
      </IconButton>
      : isCurrentUser 
      ? t('iam.yourAccount')
      : t('iam.notAllowed') 
    );
  }

  const columns = [
    { field: 'name', header: t('common.labels.name') },
    { field: 'email', header: t('common.fields.email') },
    { field: 'role_title', header: t('common.labels.role') },
    { field: 'action', header: t('iam.editUser'), body: actionTemplate },
  ];

  return (
    <div className="user-list-wrapper-div">
      <div className="margin-left-div">
        <div className="user-invite-div">
          <p className='xl bold'>{t('iam.inviteNewUser')}</p>
          <div className='user-invite-flex-end-div'>
            <div className="p-20 right" style={{width: "20%", minWidth: "150px"}}>
              <Text
                  title={t('common.fields.firstName')}
                  value={inviteUserData.first_name}
                  required={true}
                  fullWidth={true}
                  handleChange={e => {
                    setInviteUserData({...inviteUserData, first_name: e.target.value});
                    setInviteUserErrorMessages({...inviteUserErrorMessages, first_name: null});
                  }}
              />
              {inviteUserErrorMessages.first_name ? <div className="user-invite-error-div">{inviteUserErrorMessages.first_name}</div> : ''}
            </div>
            <div className="p-20 right" style={{width: "20%", minWidth: "150px"}}>
              <Text
                  title={t('common.fields.lastName')}
                  value={inviteUserData.last_name}
                  required={true}
                  fullWidth={true}
                  handleChange={e => {
                    setInviteUserData({...inviteUserData, last_name: e.target.value});
                    setInviteUserErrorMessages({...inviteUserErrorMessages, last_name: null});
                  }}
              />
              {inviteUserErrorMessages.last_name ? <div className="user-invite-error-div">{inviteUserErrorMessages.last_name}</div> : ''}
            </div>
            <div className="p-20 right" style={{width: "30%", minWidth: "200px"}}>
              <Text
                  title={t('common.fields.email')}
                  value={inviteUserData.email}
                  required={true}
                  fullWidth={true}
                  handleChange={e => {
                    setInviteUserData({...inviteUserData, email: e.target.value});
                    setInviteUserErrorMessages({...inviteUserErrorMessages, email: null});
                  }}
              />
              {inviteUserErrorMessages.email ? <div className="user-invite-error-div">{inviteUserErrorMessages.email}</div> : ''}
            </div>
            <div>
              <DropdownSelect
                options={Utils.processRoleInvitation(userRole, jobRoles)}
                label={t('iam.userRoles')}
                handleChange={e => {
                  setInviteUserErrorMessages({...inviteUserErrorMessages, role_id: null});
                  const selectedRole = findRoleBySelectValue(e.target.value);
                  setInviteUserData({
                    ...inviteUserData,
                    role_id: selectedRole?.id ?? '',
                    role_title: selectedRole?.title ?? ''
                  });
                }}
                option={inviteUserData.role_id ?? ''}
              />
              {inviteUserErrorMessages.role_id ? <div className="user-invite-error-div">{inviteUserErrorMessages.role_id}</div> : ''}
            </div>
            <div style={{width: '15%', display: 'flex', justifyContent: 'right'}}>
              <WriteAccessGuard>
                <button className="mazhrBtn default-green" onClick={sendInvite}>{t('iam.sendInvite')}</button>
              </WriteAccessGuard>
            </div>
            
          </div>
        </div>
        <div className="user-list-inner-div">
          <DataTable 
            value={users.sort((a, b) => a.role_id - b.role_id)} 
            sortField="role_id" 
            selectionMode="single" 
            selection={currentUser}
            sortOrder={1} 
            removableSort 
            paginator 
            rows={10} 
            rowsPerPageOptions={[10, 15, 20]} 
            emptyMessage={t('emptyStates.noUsers')}
          >
            {columns.map((column, i) => (
              <Column
                  key={i}
                  field={column.field}
                  header={column.header}
                  body={column.body}
                  filter
                  filterPlaceholder={t('common.actions.search')}
              />
            ))}
          </DataTable>
        </div>
      </div>
      <Modal show={openEditUserModal} onHide={() => setOpenEditUserModal(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('iam.editUser')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editedUser ? (
            <>
            <div className='flex'>
              <span className="p-20 right" style={{width: "20%"}}>
                <Text
                  title={t('common.fields.firstName')}
                  value={editedUser.first_name}
                  fullWidth={true}
                  handleChange={e => {
                    setEditedUserErrorMessages({...editedUserErrorMessages, first_name: null});
                    setEditedUser({...editedUser, first_name: e.target.value});
                  }}
                />
                {editedUserErrorMessages.first_name ? <div className="user-invite-error-div">{editedUserErrorMessages.first_name}</div> : ''}
              </span>
              <span className="p-20 right" style={{width: "20%"}}>
                <Text
                  title={t('common.fields.lastName')}
                  value={editedUser.last_name}
                  fullWidth={true}
                  handleChange={e => {
                    setEditedUserErrorMessages({...editedUserErrorMessages, last_name: null});
                    setEditedUser({...editedUser, last_name: e.target.value});
                  }}
                />
                {editedUserErrorMessages.last_name ? <div className="user-invite-error-div">{editedUserErrorMessages.last_name}</div> : ''}
              </span>
              <span className="p-20 right" style={{width: "40%"}}>
                <Text
                  title={t('common.fields.email')}
                  value={editedUser.email}
                  fullWidth={true}
                  disabled={true}
                  handleChange={e => true}
                />
              </span>
              <span>
                <DropdownSelect
                  style={{width: "20%"}}
                  options={Utils.processRoleInvitation(userRole, jobRoles)}
                  label={t('iam.userRole')}
                  handleChange={e => {
                    setEditedUserErrorMessages({...editedUserErrorMessages, role_id: null});
                    const selectedRole = findRoleBySelectValue(e.target.value);
                    if (!selectedRole) {
                      return;
                    }
                    setEditedUser({
                      ...editedUser,
                      role_id: selectedRole.id,
                      role_title: selectedRole.title
                    });
                  }}
                  option={editedUser.role_id ?? ''}
                />
                {editedUserErrorMessages.role_id ? <div className="user-invite-error-div">{editedUserErrorMessages.role_id}</div> : ''}
              </span>
            </div>
            <div className='flex flex-row mar-25 top justify-content-space-between'>
              <WriteAccessGuard>
                <button className="mazhrBtn default-green" onClick={() => {
                  updateUserAccount(editedUser.id, {
                    first_name: editedUser.first_name,
                    last_name: editedUser.last_name,
                    role_id: Number(editedUser.role_id)
                  });
                }}>{t('common.actions.save')}</button>
              </WriteAccessGuard>
              <button className="mazhrBtn light-grey" onClick={() => {
                setOpenEditUserModal(false);
              }}>{t('common.actions.cancel')}</button>
              {Constants.ADMINISTRATIVE_USER_ROLES.includes(userRole) && (
                <WriteAccessGuard>
                  <button className="mazhrBtn white background-red" onClick={() => {
                    deleteUserAccount(editedUser.id);
                  }}>{t('iam.deleteUser')}</button>
                </WriteAccessGuard>
              )}
            </div>
            </>
          ) : t('emptyStates.selectUserToEdit')}
        </Modal.Body>
      </Modal>
    </div>
  );
}
